import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Dropdown,
  Progress,
  Segmented,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DownOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import useAuth from "../../hooks/useAuth";
import { fetchCategories } from "../../service/categorization";
import {
  changeDocumentCategory,
  clearDocumentReview,
  getMatterProcessingPage,
  getMatterReviewResolution,
  markDocumentNotNeeded,
  markDocumentRequired,
  retryDocument,
} from "../../service/matterProcessing";
// Proven read-only Apryse-in-modal viewer (loads a document by id via the
// streaming endpoint). Reused here as the row "quick preview" pop-up.
import DocumentPreviewModal from "../../components/Categorization/GoldStandardViewerModal";

const { Title, Text } = Typography;

// Fast while work is active; slow while the last snapshot is terminal. The
// idle poll is load-bearing for retries started outside this browser (admin
// tools / another tab / automatic recovery): without it, a page that once saw
// processing=0 disabled polling forever and kept showing stale failures.
export const ACTIVE_POLL_INTERVAL_MS = 8000;
export const IDLE_POLL_INTERVAL_MS = 30_000;
export const matterProcessingPollInterval = (processingCount) =>
  Number(processingCount || 0) > 0
    ? ACTIVE_POLL_INTERVAL_MS
    : IDLE_POLL_INTERVAL_MS;

const STATUS_META = {
  processed: { color: "success", label: "Processed" },
  processing: { color: "processing", label: "Processing" },
  errored: { color: "error", label: "Errored" },
};

// Errored first, then still-processing, then done — the operator cares about
// problems at the top.
const STATUS_SORT_RANK = { errored: 0, processing: 1, processed: 2 };

const STAGE_COLUMNS = [
  ["extracted", "Extracted"],
  ["categorized", "Categorized"],
  ["graphed", "Graphed"],
  ["embedded", "Embedded"],
];

const REVIEW_META = {
  not_needed: { color: "default", label: "Not needed" },
  required: { color: "volcano", label: "Required" },
  // The system's own decision after the PDF health check: the file cannot be
  // read (cut off, empty, password-protected, not a PDF, damaged). The claim
  // file proceeds without it; it waits for a replacement. Retry never helps.
  unreadable: { color: "red", label: "Unreadable — needs replacement" },
};

// Whether Retry could possibly help. The backend says so per row
// (`retry_allowed`, false once the file itself has been ruled unreadable);
// older payloads without the field keep the old behaviour.
const retryAllowed = (row) => row.retry_allowed !== false;

// Whether a document "needs a decision". Prefer the backend's per-row
// `needs_decision` (computed with the pipeline hold-gate's exact rule,
// dms_backend #991); fall back to the older local approximation (errored and
// not yet marked "not needed") when the field is absent.
const needsDecision = (row) =>
  typeof row.needs_decision === "boolean"
    ? row.needs_decision
    : row.processed_status === "errored" && row.review_decision !== "not_needed";

const FILTERS = ["All", "Needs decision", "Errored", "Processing", "Processed"];

const stageIcon = (state) => {
  if (state === "done") {
    return <CheckCircleFilled style={{ color: "#52c41a" }} />;
  }
  if (state === "error") {
    return <CloseCircleFilled style={{ color: "#ff4d4f" }} />;
  }
  return <MinusCircleOutlined style={{ color: "#bfbfbf" }} />;
};

const formatChars = (n) => {
  const value = Number(n || 0);
  if (value <= 0) return "—";
  return value.toLocaleString();
};

// A green "Graphed" tick only means the graph step finished and filed its
// result; the backend records success whether the extraction found three
// hundred facts or none. Zero is often legitimate (VA boilerplate notices,
// blank questionnaires, reprinted regulations), so this column shows the
// count rather than treating an empty result as a failure. `null` means the
// document is never graphed by design and must not read as an empty result.
const GRAPH_FACTS_EMPTY_HINT =
  "Graphed, but no facts were extracted. Often correct for boilerplate or " +
  "blank forms — worth a look on a substantive document.";

const graphFactsCell = (facts) => {
  if (facts == null) {
    return <Tooltip title="Not graphed for this document type."><Text type="secondary">n/a</Text></Tooltip>;
  }
  const value = Number(facts);
  if (value <= 0) {
    return (
      <Tooltip title={GRAPH_FACTS_EMPTY_HINT}>
        <Text type="warning">0</Text>
      </Tooltip>
    );
  }
  return value.toLocaleString();
};

const extractionChunkStatus = (row) =>
  row?.text_extraction_workflow?.chunk_status || null;

const graphChunkStatus = (row) => row?.graph_extraction?.chunk_status || null;

const chunkProgressPercent = (chunkStatus) => {
  const total = Number(chunkStatus?.total_chunks || 0);
  if (total <= 0) return 0;
  return Math.min(100, Math.round((Number(chunkStatus?.done_chunks || 0) / total) * 100));
};

const formatDuration = (seconds) => {
  const value = Number(seconds || 0);
  if (value <= 0) return "";
  if (value < 60) return `${Math.round(value)}s`;
  const minutes = Math.floor(value / 60);
  const remainder = Math.round(value % 60);
  return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
};

const chunkProgressLabel = (chunkStatus) => {
  const done = Number(chunkStatus?.done_chunks || 0);
  const total = Number(chunkStatus?.total_chunks || 0);
  const processing = Number(chunkStatus?.processing_chunks || 0);
  const queued = Number(chunkStatus?.on_deck_chunks || 0);
  const concurrency = Number(chunkStatus?.chunk_concurrency || 0);
  const longestActive = formatDuration(chunkStatus?.longest_active_chunk_seconds);
  if (!total) return "";
  const parts = [`${done}/${total} chunks`];
  if (processing) parts.push(`${processing} running`);
  if (queued) parts.push(`${queued} queued`);
  if (concurrency) parts.push(`×${concurrency}`);
  if (longestActive) parts.push(`longest ${longestActive}`);
  return parts.join(" · ");
};

const ChunkProgress = ({ chunkStatus, label = "" }) => {
  if (!chunkStatus?.total_chunks) return null;
  const percent = chunkProgressPercent(chunkStatus);
  const progressLabel = chunkProgressLabel(chunkStatus);
  return (
    <div style={{ minWidth: 180 }} data-testid="chunk-progress">
      <Progress
        percent={percent}
        size="small"
        status="active"
        showInfo={false}
        style={{ marginBottom: 2 }}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label ? `${label}: ${progressLabel}` : progressLabel}
      </Text>
    </div>
  );
};

const compareRowsByInitialPriority = (a, b) => {
  const rankA = STATUS_SORT_RANK[a.processed_status] ?? 9;
  const rankB = STATUS_SORT_RANK[b.processed_status] ?? 9;
  if (rankA !== rankB) return rankA - rankB;
  return String(a.name || "").localeCompare(String(b.name || ""));
};

const sortRowsForInitialDisplay = (items) =>
  [...items].sort(compareRowsByInitialPriority);

const mergeRowsPreservingOrder = (currentRows, incomingRows, { removeMissing = false } = {}) => {
  const incomingById = new Map();
  incomingRows.forEach((row) => {
    if (row?.document_id != null) incomingById.set(row.document_id, row);
  });

  const seen = new Set();
  const merged = [];

  currentRows.forEach((row) => {
    const id = row?.document_id;
    if (id == null) return;
    const next = incomingById.get(id);
    if (next) {
      merged.push(next);
      seen.add(id);
    } else if (!removeMissing) {
      merged.push(row);
      seen.add(id);
    }
  });

  incomingRows.forEach((row) => {
    const id = row?.document_id;
    if (id == null || seen.has(id)) return;
    merged.push(row);
    seen.add(id);
  });

  return merged;
};

const MatterProcessing = ({ matterId: providedMatterId, embedded = false }) => {
  const { id: routeMatterId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const matterId = providedMatterId || routeMatterId;

  const [rows, setRows] = useState([]);
  const [matterLabel, setMatterLabel] = useState("");
  const [summary, setSummary] = useState({ processed: 0, processing: 0, errored: 0 });
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("All");
  const [categories, setCategories] = useState([]);
  const [savingCategoryFor, setSavingCategoryFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [needsDecisionCount, setNeedsDecisionCount] = useState(0);
  const [actionPendingFor, setActionPendingFor] = useState(null);

  const isMountedRef = useRef(false);
  const loadInFlightRef = useRef(false);
  // Monotonic id per load. Only the NEWEST load may write shared state (rows,
  // flags, error text, banner count): a superseded request that settles late —
  // e.g. one that hung until the request timeout — must not clobber the load
  // that replaced it, and must not clear the in-flight/refreshing flags the
  // newer load now owns.
  const loadSeqRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async ({ isInitial = false, force = false } = {}) => {
      if (!matterId) return;
      // Guard against overlapping loads stacking up against a slow endpoint
      // (the per-doc verification is heavy on large matters, and the poll fires
      // every few seconds). User-initiated Retry/Refresh pass `force`: while a
      // request is hung, waiting for it made both buttons silent no-ops — the
      // exact lock-up users hit when the backend stalled on live Temporal
      // status (Armijo, 2026-07-27). A forced load supersedes the hung one via
      // loadSeqRef; the request timeout eventually reaps it.
      if (loadInFlightRef.current && !force) return;
      loadInFlightRef.current = true;
      loadSeqRef.current += 1;
      const seq = loadSeqRef.current;
      const isCurrent = () =>
        isMountedRef.current && loadSeqRef.current === seq;
      if (isInitial) setLoading(true);
      else setRefreshing(true);
      try {
        // Render the FIRST page immediately, then append the rest in the
        // background — never make the user stare at a spinner while a large
        // matter's full document list loads. `summary` is matter-wide on every
        // response, so the headline counts are correct from the first page.
        const PAGE = 200;
        const first = await getMatterProcessingPage(matterId, { offset: 0, limit: PAGE });
        if (!isCurrent()) return;
        const acc = [...(first.documents || [])];
        setRows((prev) =>
          isInitial
            ? sortRowsForInitialDisplay(acc)
            : mergeRowsPreservingOrder(prev, acc)
        );
        setMatterLabel(first.matter?.label || "");
        setSummary(first.summary || { processed: 0, processing: 0, errored: 0 });
        setTotalDocuments(Number(first.total_documents || 0));
        setLoadError("");
        setLoading(false); // first page is on screen — stop the full-page spinner

        // Banner count. The processing-page payload carries a matter-wide
        // `needs_decision_count` computed with the pipeline hold-gate's exact
        // rule (dms_backend #991) — the old review-resolution endpoint applied
        // the stricter full-processing rule and over-reported. Fall back to
        // that endpoint only when the backend hasn't rolled out the new field.
        if (typeof first.needs_decision_count === "number") {
          setNeedsDecisionCount(first.needs_decision_count);
        } else {
          // Best-effort fallback; must not block the table render. Skip the
          // result if a newer load has started meanwhile — a stale
          // needs_review_count must not overwrite the authoritative count.
          getMatterReviewResolution(matterId)
            .then((res) => {
              if (isCurrent()) {
                setNeedsDecisionCount(Number(res?.needs_review_count || 0));
              }
            })
            .catch(() => {});
        }

        // Append the remaining pages, updating the table as each arrives.
        const total = Number(first.total_documents || 0);
        let offset = acc.length;
        while (offset < total && isCurrent()) {
          const next = await getMatterProcessingPage(matterId, { offset, limit: PAGE });
          if (!isCurrent()) return;
          const rows = next.documents || [];
          if (rows.length === 0) break;
          acc.push(...rows);
          setRows((prev) =>
            isInitial
              ? sortRowsForInitialDisplay(acc)
              : mergeRowsPreservingOrder(prev, acc)
          );
          offset += rows.length;
        }
        if (!isInitial && isCurrent()) {
          setRows((prev) =>
            mergeRowsPreservingOrder(prev, acc, { removeMissing: true })
          );
        }
      } catch (err) {
        if (isCurrent()) {
          const detail =
            err?.response?.data?.detail || err?.message || "Failed to load processing status.";
          setLoadError(detail);
        }
      } finally {
        // Only the load that still owns the sequence may release the shared
        // flags; a superseded request settling late must not clear the
        // in-flight/refreshing state of the load that replaced it.
        if (loadSeqRef.current === seq) {
          loadInFlightRef.current = false;
          if (isMountedRef.current) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      }
    },
    [matterId]
  );

  useEffect(() => {
    load({ isInitial: true });
  }, [load]);

  // Load the canonical category list once (for the admin inline-edit dropdown).
  useEffect(() => {
    let active = true;
    fetchCategories()
      .then((data) => {
        if (active) setCategories(data?.categories || []);
      })
      .catch(() => {
        /* non-fatal: dropdown just won't have options */
      });
    return () => {
      active = false;
    };
  }, []);

  // Always refresh while this page is open. Poll quickly during active work
  // and slowly when the latest response is terminal so externally-started
  // retries can wake the UI without a manual Refresh. Pause while hidden.
  useEffect(() => {
    if (!matterId) return undefined;
    const tick = () => {
      if (document.hidden) return;
      load({ isInitial: false });
    };
    const interval = matterProcessingPollInterval(summary.processing);
    const handle = setInterval(tick, interval);
    return () => clearInterval(handle);
  }, [matterId, summary.processing, load]);

  const handleCategoryChange = useCallback(async (row, nextCategory) => {
    if (!nextCategory || nextCategory === row.category) return;
    setSavingCategoryFor(row.document_id);
    try {
      await changeDocumentCategory(row.document_id, nextCategory);
      if (!isMountedRef.current) return;
      setRows((prev) =>
        prev.map((r) =>
          r.document_id === row.document_id ? { ...r, category: nextCategory } : r
        )
      );
      message.success(`Category updated to "${nextCategory}".`);
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Could not change the category.";
      message.error(detail);
    } finally {
      if (isMountedRef.current) setSavingCategoryFor(null);
    }
  }, []);

  // Run a per-document action (retry / mark / clear), then reload so the row's
  // status + the banner reflect the new state. Errors surface as a toast.
  const runAction = useCallback(
    async (row, successMessage, fn) => {
      setActionPendingFor(row.document_id);
      try {
        await fn();
        if (!isMountedRef.current) return;
        message.success(successMessage);
        await load({ isInitial: false });
      } catch (err) {
        const detail =
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "That action failed. Please try again.";
        message.error(detail);
      } finally {
        if (isMountedRef.current) setActionPendingFor(null);
      }
    },
    [load]
  );

  const sortedRows = useMemo(() => {
    return rows.filter((row) => {
      if (filter === "All") return true;
      if (filter === "Needs decision") return needsDecision(row);
      return row.processed_status === filter.toLowerCase();
    });
  }, [rows, filter]);

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ label: c, value: c })),
    [categories]
  );

  const columns = useMemo(
    () => [
      {
        title: "Document",
        dataIndex: "name",
        key: "name",
        width: 260,
        render: (name, row) => (
          <Button
            type="link"
            style={{ padding: 0, height: "auto", whiteSpace: "normal", textAlign: "left" }}
            onClick={() =>
              setPreview({
                documentId: row.document_id,
                filename: name,
                category: row.category,
              })
            }
          >
            {name || `Document ${row.document_id}`}
          </Button>
        ),
      },
      {
        title: "Status",
        dataIndex: "processed_status",
        key: "status",
        width: 130,
        render: (status) => {
          const meta = STATUS_META[status] || { color: "default", label: status };
          return <Tag color={meta.color}>{meta.label}</Tag>;
        },
      },
      {
        title: "Stages",
        key: "stages",
        width: 220,
        render: (_, row) => (
          <div style={{ display: "flex", gap: 12 }}>
            {STAGE_COLUMNS.map(([key, label]) => (
              <Tooltip key={key} title={`${label}: ${row.stages?.[key] || "pending"}`}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {stageIcon(row.stages?.[key])}
                </span>
              </Tooltip>
            ))}
          </div>
        ),
      },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 240,
        render: (category, row) =>
          isAdmin ? (
            <Select
              size="small"
              style={{ width: 220 }}
              value={category || undefined}
              placeholder="—"
              options={categoryOptions}
              loading={savingCategoryFor === row.document_id}
              disabled={savingCategoryFor === row.document_id}
              onChange={(value) => handleCategoryChange(row, value)}
              showSearch
              optionFilterProp="label"
            />
          ) : (
            <Text>{category || "—"}</Text>
          ),
      },
      {
        title: "Pages",
        dataIndex: "total_pages",
        key: "pages",
        width: 80,
        render: (p) => (p == null ? "—" : p),
      },
      {
        title: "Text",
        dataIndex: "markdown_chars",
        key: "text",
        width: 220,
        render: (chars, row) => {
          const graphStatus = graphChunkStatus(row);
          if (graphStatus?.total_chunks && row.processed_status === "processing") {
            return <ChunkProgress chunkStatus={graphStatus} label="Graph" />;
          }
          const extractionStatus = extractionChunkStatus(row);
          if (extractionStatus?.total_chunks && row.processed_status === "processing") {
            return <ChunkProgress chunkStatus={extractionStatus} />;
          }
          return formatChars(chars);
        },
      },
      {
        title: "Facts",
        dataIndex: "graph_facts",
        key: "graph_facts",
        width: 90,
        render: (facts) => graphFactsCell(facts),
      },
      {
        title: "Why it failed",
        dataIndex: "failure_reason",
        key: "failure_reason",
        width: 220,
        render: (reason, row) =>
          row.processed_status === "errored" && reason ? (
            <Tooltip title={reason}>
              {/* Plain CSS truncation instead of antd's ResizeObserver-based
                  `ellipsis` prop: inside this embedded/expandable table the
                  observer can measure before the row has real width, leaving
                  the untruncated text to spill across the Review/Actions
                  columns. A fixed-width box with overflow:hidden can't do
                  that regardless of when it mounts. */}
              <Text
                type="danger"
                style={{
                  display: "block",
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {reason}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: "Review",
        dataIndex: "review_decision",
        key: "review_decision",
        width: 110,
        render: (decision, row) => {
          const meta = REVIEW_META[decision];
          if (!meta) return <Text type="secondary">—</Text>;
          const tag = <Tag color={meta.color}>{meta.label}</Tag>;
          // For an unreadable file, say what the health check actually saw
          // ("The file stops at exactly 4 MB…") on hover.
          return decision === "unreadable" && row.file_health_detail ? (
            <Tooltip title={row.file_health_detail}>{tag}</Tooltip>
          ) : (
            tag
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        render: (_, row) => {
          const busy = actionPendingFor === row.document_id;
          const items = [];
          if (row.processed_status === "errored" && retryAllowed(row)) {
            items.push({
              key: "retry",
              label: "Retry",
              onClick: () =>
                runAction(row, "Retry queued.", () =>
                  retryDocument(matterId, row.document_id)
                ),
            });
          }
          if (row.review_decision !== "not_needed") {
            items.push({
              key: "not-needed",
              label: 'Mark "not needed"',
              onClick: () =>
                runAction(row, 'Marked "not needed".', () =>
                  markDocumentNotNeeded(matterId, row.document_id)
                ),
            });
          }
          if (row.review_decision !== "required") {
            items.push({
              key: "required",
              label: 'Mark "required"',
              onClick: () =>
                runAction(row, 'Marked "required".', () =>
                  markDocumentRequired(matterId, row.document_id)
                ),
            });
          }
          if (row.review_decision && row.review_decision !== "none") {
            items.push({
              key: "clear",
              label: "Clear decision",
              onClick: () =>
                runAction(row, "Decision cleared.", () =>
                  clearDocumentReview(matterId, row.document_id)
                ),
            });
          }
          if (items.length === 0) {
            return <Text type="secondary">—</Text>;
          }
          return (
            <Dropdown menu={{ items }} trigger={["click"]} disabled={busy}>
              <Button size="small" loading={busy} data-testid={`actions-${row.document_id}`}>
                Actions <DownOutlined />
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [
      isAdmin,
      categoryOptions,
      savingCategoryFor,
      handleCategoryChange,
      actionPendingFor,
      runAction,
      matterId,
    ]
  );

  const tableWrapRef = useRef(null);
  const [tableScrollY, setTableScrollY] = useState(420);
  useEffect(() => {
    const recompute = () => {
      if (embedded) {
        setTableScrollY(520);
        return;
      }
      const node = tableWrapRef.current;
      const top = node ? node.getBoundingClientRect().top : 220;
      // Let the antd table body scroll INTERNALLY (sticky header), sized to the
      // space left below it in the viewport. Robust regardless of the parent
      // height chain — the app shell disables its own scroll, and the previous
      // flex+overflow container did not reliably create a scroll area here.
      // No pagination — every document renders in one internally-scrolling
      // list. Reserve ~80px for the table's own sticky header + a little
      // bottom breathing room.
      const next = Math.max(200, Math.floor(window.innerHeight - top - 80));
      setTableScrollY(next);
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [embedded, loading, sortedRows.length]);

  return (
    <div
      style={{
        padding: embedded ? "12px 0 0" : "16px 24px",
        height: embedded ? "auto" : "100vh",
        overflow: embedded ? "visible" : "hidden",
        boxSizing: "border-box",
      }}
      data-testid="matter-processing-page"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        {!embedded && (
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/matter/${matterId}`)}
            aria-label="Back to matter"
          >
            Back to matter
          </Button>
        )}
        <Title level={4} style={{ margin: 0 }}>
          Document Processing
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          {matterLabel || `Matter ${matterId}`}
        </Text>
        <Button
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={() => load({ isInitial: false, force: true })}
        >
          Refresh
        </Button>
        <span style={{ flex: 1 }} />
        <Tag color="success">{summary.processed} processed</Tag>
        <Tag color="processing">{summary.processing} processing</Tag>
        <Tag color="error">{summary.errored} errored</Tag>
        <Text type="secondary">{totalDocuments} total</Text>
      </div>

      {needsDecisionCount > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          data-testid="needs-decision-banner"
          message={`${needsDecisionCount} document${
            needsDecisionCount === 1 ? "" : "s"
          } ${needsDecisionCount === 1 ? "needs" : "need"} your decision`}
          description="Retry them, or mark them “not needed,” so this matter can finish processing."
          action={
            <Button size="small" onClick={() => setFilter("Needs decision")}>
              Show them
            </Button>
          }
        />
      )}

      <div style={{ marginBottom: 12 }}>
        <Segmented options={FILTERS} value={filter} onChange={setFilter} />
      </div>

      {/* Scrollable middle: the document list scrolls here while everything
          above stays fixed. minHeight:0 is required for overflow to work on a
          flex child. */}
      <div ref={tableWrapRef}>
        {loadError ? (
          <div style={{ padding: 24 }}>
            <Text type="danger">{loadError}</Text>{" "}
            <Button
              type="link"
              onClick={() => load({ isInitial: true, force: true })}
            >
              Retry
            </Button>
          </div>
        ) : loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            rowKey="document_id"
            columns={columns}
            dataSource={sortedRows}
            pagination={false}
            // scroll.x pins every column to its declared `width` so the
            // sticky header (rendered as a separate table from the body
            // when scroll.y is set) can't compute different widths than the
            // body — without it, a wide cell like "Why it failed" desyncs
            // the two and its content spills across later columns.
            scroll={{ x: "max-content", y: tableScrollY }}
            size="middle"
          />
        )}
      </div>

      <DocumentPreviewModal
        open={!!preview}
        documentId={preview?.documentId || null}
        filename={preview?.filename || ""}
        category={preview?.category || ""}
        onClose={() => setPreview(null)}
      />
    </div>
  );
};

export default MatterProcessing;
